import { useContext, createContext, useEffect, useState } from "react";
import {auth, db} from '../firebase'
import { 
    GoogleAuthProvider,
    signInWithPopup,
    // signInWithRedirect,
    signOut,
    deleteUser,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    sendEmailVerification,
    updateProfile
 } from "firebase/auth";
import { doc, setDoc, getDoc, deleteDoc, collection, query, where, getDocs } from "firebase/firestore";

const AuthContext = createContext()

export const AuthContextProvider = ({children}) => {

    const [user, setUser]  = useState(null)
    const [loading, setLoading] = useState(true)
    const [needsRoleSelection, setNeedsRoleSelection] = useState(false)

    // Google Sign In
    const googleSignIn = async () => {
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            console.log("sign in successful");
            let { uid, displayName, email, photoURL } = result.user;
            console.log({uid, displayName, email, photoURL});
            console.log(result.user);
            
            // Save Google user data to Firestore if not already exists
            const userDoc = await getDoc(doc(db, "users", result.user.uid));
            if (!userDoc.exists()) {
                await setDoc(doc(db, "users", result.user.uid), {
                    uid: result.user.uid,
                    name: displayName || "Google User",
                    email: email,
                    createdAt: new Date(),
                    emailVerified: result.user.emailVerified
                });
            }
            
            return result;
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    // Email/Password Sign Up
    const emailSignUp = async (email, password, name, role) => {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            
            // Update the user's display name
            await updateProfile(user, {
                displayName: name
            });

            // Send email verification
            await sendEmailVerification(user);

            // Save user data to Firestore
            await setDoc(doc(db, "users", user.uid), {
                uid: user.uid,
                name: name,
                email: email,
                role: role,
                createdAt: new Date(),
                emailVerified: false
            });

            return userCredential;
        } catch (error) {
            console.error("Error signing up:", error);
            throw error;
        }
    }

    // Email/Password Sign In
    const emailSignIn = async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            return userCredential;
        } catch (error) {
            console.error("Error signing in:", error);
            throw error;
        }
    }

    // Forgot Password
    const forgotPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
            return true;
        } catch (error) {
            console.error("Error sending password reset email:", error);
            throw error;
        }
    }

    // Resend Email Verification
    const resendEmailVerification = async () => {
        try {
            if (user && !user.emailVerified) {
                await sendEmailVerification(user);
                return true;
            }
            throw new Error("No user logged in or email already verified");
        } catch (error) {
            console.error("Error sending email verification:", error);
            throw error;
        }
    }

    // Update User Role
    const updateUserRole = async (role) => {
        try {
            if (user) {
                await setDoc(doc(db, "users", user.uid), {
                    role: role
                }, { merge: true });
                setNeedsRoleSelection(false);
                return true;
            }
            throw new Error("No user logged in");
        } catch (error) {
            console.error("Error updating user role:", error);
            throw error;
        }
    }

    // Skip Role Selection
    const skipRoleSelection = () => {
        setNeedsRoleSelection(false);
    }

    // Logout
    const logOut = () => {
        return signOut(auth);
    }

    const deleteAccount = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error("No user logged in");
        }
        const uid = currentUser.uid;
        const userRef = doc(db, "users", uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
            await deleteDoc(userRef);
        }
        const reportsQuery = query(collection(db, "sharedReports"), where("userId", "==", uid));
        const reportsSnap = await getDocs(reportsQuery);
        await Promise.all(reportsSnap.docs.map(d => deleteDoc(d.ref)));
        await deleteUser(currentUser);
    };

    useEffect(() =>{
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser)
            setLoading(false)
            console.log('User ', currentUser);
            
            // Check if user needs role selection
            if (currentUser) {
                try {
                    const userDoc = await getDoc(doc(db, "users", currentUser.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        setNeedsRoleSelection(!userData.role);
                    } else {
                        setNeedsRoleSelection(true);
                    }
                } catch (error) {
                    console.error("Error checking user role:", error);
                    setNeedsRoleSelection(true);
                }
            } else {
                setNeedsRoleSelection(false);
            }
        })
        return () => {
            unsubscribe();
        }
    }, [])

    return(
        <AuthContext.Provider value={{
            googleSignIn, 
            emailSignUp, 
            emailSignIn, 
            forgotPassword, 
            resendEmailVerification, 
            updateUserRole,
            skipRoleSelection,
            logOut,
            deleteAccount,
            user, 
            loading,
            needsRoleSelection
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export const UserAuth = () => {
    return useContext(AuthContext)
}